import * as React from 'react'
import {Util} from 'yougile-util'

// eslint-disable-next-line max-lines-per-function
export const createSyncedEnhancerContainer = <
  Props,
  State,
  ViewContract,
  ViewProps extends ViewContract
>(
  View: React.ComponentType<ViewProps>,
  subscribe: (update: () => void, props: Props, getState: () => State) => void,
  unsubscribe: (update: () => void) => void,
  stateFromProps: (props: Props, prevState?: State) => State | undefined,
  resubscribeOnPropChange?: () => Array<keyof Props>,
  lazyState?: State
) => {
  return class SyncedEnhancerContainer extends React.Component<Props, State> {
    _isMounted = false

    constructor(props: Props) {
      super(props)

      if (lazyState) {
        this.state = Object.assign({}, lazyState, {isLoading: true})
        setTimeout(() => {
          this.update(props)
          this.subscribe(props)
        }, 0)
      } else {
        this.state = stateFromProps(props) ?? ({} as State)
        this.subscribe(props)
      }
    }

    public override componentDidMount(): void {
      this._isMounted = true
    }

    public override componentWillUnmount(): void {
      this.unsubscribe()
      this._isMounted = false
    }

    public override componentDidUpdate(
      prevProps: Props,
      _prevState: State
    ): void {
      const shouldResubscribe =
        !resubscribeOnPropChange ||
        Util.uniq(resubscribeOnPropChange()).reduce(
          (acc: boolean, prop: keyof Props) =>
            acc || this.props[prop] !== prevProps[prop],
          false
        )

      if (shouldResubscribe) {
        this.resubscribe(this.props)
      }
    }

    public override render(): React.ReactNode {
      const Comp = View as any
      return <Comp {...this.props} {...this.state} />
    }

    public subscribe(props: Props): void {
      subscribe(this.update, props, () => this.state)
    }

    public unsubscribe(): void {
      unsubscribe(this.update)
    }

    public resubscribe(props: Props): void {
      this.unsubscribe()
      this.subscribe(props)
      this.update(props)
    }

    public update = (props: Props = this.props) => {
      const state = stateFromProps(props, this.state)

      if (state && this._isMounted) {
        this.setState(state)
      }
    }
  }
}
